/**
 * Shared coercion for questionnaire answer values.
 *
 * Both questionnaire surfaces — the onboarding dataset and the supplier
 * qualification — map the same raw input values onto the same answer fields,
 * so the coercion lives here rather than being copied into each.
 */

/**
 * Coerce a raw input value to a number answer.
 *
 * CR-6165: number inputs arrive as strings, and `Number('')` is 0 — so an
 * emptied field used to persist a real zero, indistinguishable from the
 * supplier answering "0". A part-typed value ("1e", "-") becomes NaN, which
 * can 500 against the DecimalField behind it.
 *
 * Returns `null`, never `undefined`: JSON.stringify drops undefined keys, and
 * both bulk-save endpoints treat an absent key as "this answer is not part of
 * this payload, leave it alone". Only a present null clears a stored answer.
 *
 * Takes `unknown` because the two surfaces hold answers in different union
 * types (one includes matrix records), and anything non-numeric lands on the
 * same null result anyway.
 *
 * @param value Raw value from the input.
 * @returns the number, or null when the field is empty or not finite.
 */
export function toNumericAnswer(value: unknown): number | null {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}
